import { FC } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Clock, Info } from "lucide-react";
import { useInit } from "@follosoft/react-components";

import {
  formatTime,
  isAlertScheduleRestricted,
} from "@/utils/alerts-schedule";

const SETTINGS_GROUP = "custom_phone_number";
const START_TIME_KEY = "schedule_start_time";
const END_TIME_KEY = "schedule_end_time";
const RESTRICT_KEY = "restrict_alert_hours";

interface ScheduleBoundProps {
  label: string;
  time: string;
  timezone?: string;
}

/** One end of the window: "Start 07:00 America/Toronto". */
const ScheduleBound: FC<ScheduleBoundProps> = ({ label, time, timezone }) => (
  <div className="flex min-w-0 flex-col gap-1">
    <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
      {label}
      <Clock className="size-3 shrink-0" />
    </span>
    <span className="flex flex-wrap items-baseline gap-x-1.5">
      <span className="text-base font-semibold leading-6 tabular-nums text-primary">
        {time}
      </span>
      {timezone && (
        <span className="text-xs text-muted-foreground">{timezone}</span>
      )}
    </span>
  </div>
);

/**
 * The hours during which the company places call and text alerts, taken from
 * the init settings. Shown at sign-up/renewal so the customer knows when to
 * expect a call or text before agreeing to the service. Emails are sent
 * regardless of the schedule, so they are deliberately not mentioned.
 */
const AlertsScheduleNotice: FC = () => {
  const { t } = useTranslation(["occupants"]);
  const { settingByGroupAndKey, account } = useInit();

  const startSetting = settingByGroupAndKey(SETTINGS_GROUP, START_TIME_KEY);
  const endSetting = settingByGroupAndKey(SETTINGS_GROUP, END_TIME_KEY);
  const restrictSetting = settingByGroupAndKey(SETTINGS_GROUP, RESTRICT_KEY);

  // Without restrict_alert_hours the backend ignores the window, so showing
  // the times would promise a limit that does not exist.
  const restricted = isAlertScheduleRestricted(
    restrictSetting?.attributes?.value ??
      restrictSetting?.attributes?.default_value
  );

  if (!restricted) {
    return null;
  }

  const start = formatTime(
    startSetting?.attributes?.value ?? startSetting?.attributes?.default_value
  );
  const end = formatTime(
    endSetting?.attributes?.value ?? endSetting?.attributes?.default_value
  );

  if (!start || !end) {
    return null;
  }

  // The backend resolves the window in the account's timezone, so show it.
  const timezone = account?.attributes?.timezone;

  return (
    <div className="w-full shrink-0 rounded-xl bg-card p-4 lg:w-[418px]">
      <div className="flex items-center gap-2 text-sm font-semibold leading-5 text-card-foreground">
        <Info className="size-4 shrink-0 text-primary" />
        {t("occupants:occupants-schedule-title", "Call and text alert schedule")}
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-muted px-3 py-2.5">
        <ScheduleBound
          label={t("occupants:occupants-schedule-start", "Start")}
          time={start}
          timezone={timezone}
        />
        <ArrowRight className="size-5 shrink-0 text-foreground" />
        <ScheduleBound
          label={t("occupants:occupants-schedule-end", "End")}
          time={end}
          timezone={timezone}
        />
      </div>
    </div>
  );
};

export default AlertsScheduleNotice;
